/**
 * A session's Changes, in place of its thread (RFC 0313 3h, canvas 13).
 *
 * The ± dropdown is the short list; this is the long one. ONE LIST, newest
 * first: every document change (with the same undo the document's History
 * column carries, because both read the document's change graph), every file
 * write, and every commit on the session's branch, in the order they happened,
 * because a session is a sequence of things it did. A file is named by its
 * path inside the project, never by where this Mac keeps it. Code has no undo
 * here: a commit is undone by another commit.
 *
 * Only the pane body changes. The header stays one row, and ← Thread goes back.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import {
  type HistoryChange,
  projectSessionCode,
  sessionDocumentChanges,
  undoChange,
  undoSessionChanges,
} from '@mod/core/model'
import { ArrowLeft, FileCode2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { CommitRow, LineCounts } from '../../services/code/CommitRow'
import { codeFileId, worktreeRef } from '../../services/code/codeFile'
import {
  type CodeCommit,
  useBranchCommits,
} from '../../services/code/codeHistory'
import { useProjectFolderPath } from '../../services/code/projectFolder'
import {
  dayLabel,
  RowAction,
  RowSummary,
  time,
} from '../../services/history/historyRows'
import { readLocalPerson } from '../../services/identity/localPerson'
import {
  useDocument,
  useDocuments,
} from '../../services/world/repo/useDocument'
import {
  type FileEdit,
  lastSegments,
  type SessionChanges,
} from './sessionChanges'

function by() {
  const person = readLocalPerson()
  return person
    ? { authorId: person.id, by: { id: person.id, name: person.name } }
    : {}
}

function clip(s: string, n = 60): string {
  const one = s.replace(/\s+/g, ' ').trim()
  return one.length > n ? `${one.slice(0, n - 1)}…` : one
}

/** What a row did, in a line. Pure, for the test. */
export function rowLine(row: HistoryChange): string {
  if (row.text) {
    const { removed, added } = row.text
    if (removed && added) return `${clip(removed, 28)} → ${clip(added, 28)}`
    return added ? `+ ${clip(added)}` : `− ${clip(removed)}`
  }
  if (row.fields?.length) return row.fields.map((f) => f.path).join(', ')
  return row.note ?? 'a change'
}

/** One of this session's document changes, with the document it is in. */
export interface TimelineChange {
  documentId: string
  title: string
  row: HistoryChange
}

/** Every change across the session's documents, newest first. Pure. */
export function orderChanges(
  perDocument: { documentId: string; title: string; rows: HistoryChange[] }[],
): TimelineChange[] {
  return perDocument
    .flatMap((d) =>
      d.rows.map((row) => ({ documentId: d.documentId, title: d.title, row })),
    )
    .sort((a, b) => b.row.time - a.row.time)
}

/**
 * How a written file is named and opened. By its path in the checkout it
 * landed in when that was recorded, else by its path under the project's
 * folder on this Mac; a file outside both is named by its last two segments
 * and marked, because it is not this project's. Pure, for the test.
 */
export function fileLabel(
  edit: Pick<FileEdit, 'path' | 'writtenOn'>,
  folder: string | null,
  sourceId?: string,
): { label: string; codeId: string | null; outside: boolean } {
  const on = edit.writtenOn
  if (on) {
    return {
      label: on.relPath,
      codeId: on.branch
        ? codeFileId(on.relPath, worktreeRef(on.branch), sourceId)
        : null,
      outside: false,
    }
  }
  const root = folder?.replace(/\/+$/, '')
  if (root && edit.path.startsWith(`${root}/`)) {
    const rel = edit.path.slice(root.length + 1)
    return {
      label: rel,
      codeId: codeFileId(rel, null, sourceId),
      outside: false,
    }
  }
  return { label: lastSegments(edit.path), codeId: null, outside: true }
}

/** One row of the session's Changes. `at` is millis. */
export type ChangeItem =
  | { kind: 'document'; key: string; at: number; change: TimelineChange }
  | { kind: 'file'; key: string; at: number; edit: FileEdit }
  | { kind: 'commit'; key: string; at: number; commit: CodeCommit }

/**
 * Document changes, file writes and commits as one list, newest first. A write
 * from a transcript older than its timestamps sorts last rather than guessing.
 * Pure, for the test.
 */
export function mergeChanges(
  documents: TimelineChange[],
  edits: FileEdit[],
  commits: CodeCommit[],
): ChangeItem[] {
  const items: ChangeItem[] = [
    ...documents.map((change) => ({
      kind: 'document' as const,
      key: `d:${change.row.hash}`,
      at: change.row.time * 1000,
      change,
    })),
    ...edits.map((edit) => ({
      kind: 'file' as const,
      key: `f:${edit.key}`,
      at: edit.at ?? 0,
      edit,
    })),
    ...commits.map((commit) => ({
      kind: 'commit' as const,
      key: `c:${commit.sha}`,
      at: Date.parse(commit.date) || 0,
      commit,
    })),
  ]
  return items.sort((a, b) => b.at - a.at)
}

const card = 'mx-2 mb-2 rounded-lg border border-border/70 bg-card px-2.5 py-2'

export function SessionChangesPanel({
  repo,
  projectId,
  sessionId,
  changes,
  includeFiles = true,
  documents,
  onBack,
  onOpenDocument,
}: {
  repo: Repo
  projectId: string
  sessionId: string
  changes: SessionChanges
  /** File history belongs to the installed Review contribution. */
  includeFiles?: boolean
  documents?: { id: string; title: string }[]
  /** Back to the thread. */
  onBack: () => void
  onOpenDocument?: (id: string) => void
}) {
  const titles = useMemo(
    () => new Map((documents ?? []).map((d) => [d.id, d.title])),
    [documents],
  )
  const docs = useMemo(
    () =>
      changes.documents.filter(
        (d): d is typeof d & { documentId: string } => !!d.documentId,
      ),
    [changes.documents],
  )
  const [result, setResult] = useState<string | null>(null)
  const [running, setRunning] = useState(false)

  // The session's document changes, read off each document's change graph.
  const ids = useMemo(
    () => docs.map((d) => d.documentId as AnyDocumentId),
    [docs],
  )
  const docMap = useDocuments<unknown>(ids)
  const documentChanges = useMemo(
    () =>
      orderChanges(
        docs.map((d) => ({
          documentId: d.documentId,
          title: titles.get(d.documentId) ?? d.label,
          rows: sessionDocumentChanges(docMap.get(d.documentId), sessionId),
        })),
      ),
    [docs, titles, docMap, sessionId],
  )

  // The commits on the session's branch since it forked (3h, 3n): read from
  // git, because nothing writes them to the log yet (RFC 0316 S2).
  const [sessionDoc] = useDocument(sessionId as AnyDocumentId)
  const code = projectSessionCode(sessionDoc)
  const sourceId =
    (sessionDoc as { codeSourceId?: string } | undefined)?.codeSourceId ??
    code?.sourceId
  const commits = useBranchCommits(
    projectId,
    includeFiles ? code?.branch : undefined,
    includeFiles ? code?.baseCommit : undefined,
    sourceId,
  )

  // Where the project lives on this Mac, only to name files inside it.
  const folder = useProjectFolderPath(projectId, sourceId)

  const items = useMemo(
    () => mergeChanges(documentChanges, changes.edits, commits),
    [documentChanges, changes.edits, commits],
  )
  const now = new Date()
  let lastDay = ''

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto pb-4">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 self-start px-3 pt-3 pb-1 text-muted-foreground text-xs hover:text-foreground"
      >
        <ArrowLeft className="size-3" /> Thread
      </button>
      {items.map((item) => {
        const day = item.at ? dayLabel(item.at / 1000, now) : 'Earlier'
        const heading = day !== lastDay ? day : null
        lastDay = day
        return (
          <div key={item.key}>
            {heading ? (
              <h3 className="px-3 pt-3 pb-1 text-[11px] text-muted-foreground">
                {heading}
              </h3>
            ) : null}
            {item.kind === 'document' ? (
              <DocumentCard
                repo={repo}
                projectId={projectId}
                change={item.change}
                doc={docMap.get(item.change.documentId)}
                {...(onOpenDocument ? { onOpenDocument } : {})}
              />
            ) : item.kind === 'file' ? (
              <FileCard
                edit={item.edit}
                folder={folder}
                sourceId={sourceId}
                {...(onOpenDocument ? { onOpen: onOpenDocument } : {})}
              />
            ) : (
              <div className={card}>
                <CommitRow
                  projectId={projectId}
                  sourceId={sourceId}
                  commit={item.commit}
                  byline={`${code?.branch ?? ''} · ${time(item.at / 1000)}`}
                  {...(onOpenDocument ? { onOpen: onOpenDocument } : {})}
                />
              </div>
            )}
          </div>
        )
      })}
      {docs.length > 0 ? (
        <div className="flex items-center gap-2 px-3 pt-1">
          <button
            type="button"
            disabled={running}
            onClick={() => {
              setRunning(true)
              void undoSessionChanges(
                repo,
                projectId,
                sessionId,
                docs.map((d) => d.documentId),
                by(),
              )
                .then((r) =>
                  setResult(
                    `${r.undone} undone${r.refused ? `, ${r.refused} refused` : ''}`,
                  ),
                )
                .catch(() => setResult('Could not undo'))
                .finally(() => setRunning(false))
            }}
            className="rounded-full border border-border px-2.5 py-0.5 text-xs hover:bg-muted disabled:opacity-50"
          >
            Undo this session’s document changes
          </button>
          {result ? (
            <span className="text-[11px] text-muted-foreground">{result}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** A document change, drawn as the document's own History column draws it. */
function DocumentCard({
  repo,
  projectId,
  change: { documentId, title, row },
  doc,
  onOpenDocument,
}: {
  repo: Repo
  projectId: string
  change: TimelineChange
  doc: unknown
  onOpenDocument?: (id: string) => void
}) {
  return (
    <div className={card}>
      <div className="mb-1 flex items-center gap-1.5 text-xs">
        {onOpenDocument ? (
          <button
            type="button"
            onClick={() => onOpenDocument(documentId)}
            className="truncate text-foreground hover:underline"
          >
            {title}
          </button>
        ) : (
          <span className="truncate text-foreground">{title}</span>
        )}
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {time(row.time)}
        </span>
      </div>
      <div className="flex items-start gap-2 text-xs">
        <span className="min-w-0 flex-1 break-words">
          <RowSummary row={row} />
          {row.note ? (
            <span className="block text-[11px] text-muted-foreground">
              {row.note}
            </span>
          ) : null}
        </span>
        {doc ? (
          <RowAction
            doc={doc as never}
            row={row}
            undo={(hash) => undoChange(repo, projectId, documentId, hash, by())}
          />
        ) : null}
      </div>
    </div>
  )
}

/** One file write: its path in the project, when, and how many lines. */
function FileCard({
  edit,
  folder,
  onOpen,
  sourceId,
}: {
  edit: FileEdit
  folder: string | null
  onOpen?: (codeId: string) => void
  sourceId?: string
}) {
  const { label, codeId, outside } = fileLabel(edit, folder, sourceId)
  return (
    <div className={card}>
      <div className="flex items-center gap-1.5 text-xs">
        <FileCode2 className="size-3 shrink-0 text-muted-foreground/70" />
        {codeId && onOpen ? (
          <button
            type="button"
            onClick={() => onOpen(codeId)}
            className="min-w-0 truncate font-mono text-[11px] text-foreground hover:underline"
          >
            {label}
          </button>
        ) : (
          <span className="min-w-0 truncate font-mono text-[11px] text-foreground">
            {label}
          </span>
        )}
        {edit.at ? (
          <span className="shrink-0 text-[11px] text-muted-foreground">
            {time(edit.at / 1000)}
          </span>
        ) : null}
        <span className="ml-auto shrink-0 text-[11px]">
          <LineCounts insertions={edit.insertions} deletions={edit.deletions} />
        </span>
      </div>
      {outside ? (
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          Outside this project
        </div>
      ) : null}
    </div>
  )
}
